import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Camera,
  Upload,
  CheckCircle,
  AlertCircle,
  Smartphone,
  Image as ImageIcon,
  X,
  Loader2,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MobileUploadPage() {
  const { token } = useParams();
  const [tokenInfo, setTokenInfo] = useState(null);
  const [shopName, setShopName] = useState("AMCPhone Martinique");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const fileInputRef = useRef(null);

  
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/shop-info`)
      .then(r => r.json()).then(d => { if (d.shop_name) setShopName(d.shop_name); }).catch(() => {});
  }, []);

useEffect(() => {
    const fetchTokenInfo = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/mobile-upload/${token}/info`);
        setTokenInfo(res.data);
        // If this is an intake token, track already uploaded photos
        if (res.data.is_intake && res.data.photos_uploaded) {
          setUploadedCount(res.data.photos_uploaded);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError("Ce lien n'est pas valide.");
        } else if (err.response?.status === 410) {
          setError("Ce lien a expiré ou a déjà été utilisé.");
        } else {
          setError("Une erreur est survenue.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTokenInfo();
  }, [token]);

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newPhotos = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setPhotos((prev) => [...prev, ...newPhotos]);
  };

  const removePhoto = (index) => {
    setPhotos((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleUpload = async () => {
    if (photos.length === 0) {
      toast.error("Veuillez sélectionner au moins une photo");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      photos.forEach((photo) => {
        formData.append("photos", photo.file);
      });

      const res = await axios.post(`${API_URL}/api/mobile-upload/${token}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success(`${res.data.count} photo(s) envoyée(s) avec succès !`);
      
      // For intake tokens, allow adding more photos
      if (tokenInfo?.is_intake) {
        setUploadedCount(prev => prev + res.data.count);
        setPhotos([]); // Clear current selection
        toast.info("Vous pouvez ajouter d'autres photos si nécessaire");
      } else {
        setUploadSuccess(true);
      }
    } catch (err) {
      if (err.response?.status === 410) {
        setError("Ce lien a expiré ou a déjà été utilisé.");
      } else {
        toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
      }
    } finally {
      setUploading(false);
    }
  };

  const handleFinish = () => {
    setUploadSuccess(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">Lien invalide</h2>
            <p className="text-muted-foreground">{error}</p>
            <p className="text-sm text-muted-foreground mt-4">
              Demandez un nouveau QR code à l'atelier.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (uploadSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">Photos envoyées !</h2>
            <p className="text-muted-foreground">
              {tokenInfo?.is_intake 
                ? `${uploadedCount} photo(s) ont été ajoutées pour la prise en charge.`
                : `${photos.length || uploadedCount} photo(s) ont été ajoutées à la réparation.`
              }
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Vous pouvez fermer cette page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isIntake = tokenInfo?.is_intake;
  const photoTypeLabel = tokenInfo?.photo_type === "before" ? "avant réparation" : "après réparation";

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-md mx-auto space-y-4">
        {/* Header */}
        <Card className="bg-card border-border">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-2">
              <Smartphone className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-xl">{shopName}</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-2">
            {isIntake ? (
              <>
                <p className="text-muted-foreground">
                  Ajoutez des photos pour la prise en charge
                </p>
                {tokenInfo?.client_name && (
                  <p className="font-medium text-foreground">
                    {tokenInfo.client_name}
                  </p>
                )}
                {tokenInfo?.device_info && (
                  <p className="text-sm text-muted-foreground">
                    {tokenInfo.device_info}
                  </p>
                )}
                {uploadedCount > 0 && (
                  <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-500 text-sm">
                    ✓ {uploadedCount} photo(s) déjà envoyée(s)
                  </span>
                )}
              </>
            ) : (
              <>
                <p className="text-muted-foreground">
                  Ajoutez des photos pour la réparation
                </p>
                <p className="font-mono text-lg text-primary font-bold">
                  {tokenInfo?.ticket_number}
                </p>
                <span className="inline-block px-3 py-1 rounded-full bg-secondary text-sm">
                  Photo {photoTypeLabel}
                </span>
              </>
            )}
          </CardContent>
        </Card>

        {/* Photo Selection */}
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <input
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              onChange={handlePhotoSelect}
              ref={fileInputRef}
              className="hidden"
            />

            {/* Photo Grid */}
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-4">
                {photos.map((photo, index) => (
                  <div key={index} className="relative aspect-square">
                    <img
                      src={photo.preview}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <button
                      onClick={() => removePhoto(index)}
                      className="absolute -top-2 -right-2 p-1 bg-destructive rounded-full"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Photo Button */}
            <Button
              variant="outline"
              className="w-full h-32 border-dashed border-2 flex flex-col gap-2"
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera className="w-8 h-8 text-muted-foreground" />
              <span className="text-muted-foreground">
                {photos.length > 0 ? "Ajouter d'autres photos" : "Prendre ou choisir des photos"}
              </span>
            </Button>

            {photos.length > 0 && (
              <p className="text-center text-sm text-muted-foreground mt-2">
                <ImageIcon className="w-4 h-4 inline mr-1" />
                {photos.length} photo(s) sélectionnée(s)
              </p>
            )}
          </CardContent>
        </Card>

        {/* Upload Button */}
        {photos.length > 0 && (
          <Button
            className="w-full h-14 text-lg bg-primary hover:bg-primary/90"
            onClick={handleUpload}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 mr-2" />
                Envoyer {photos.length} photo(s)
              </>
            )}
          </Button>
        )}

        {/* Finish Button for intake tokens with uploaded photos */}
        {isIntake && uploadedCount > 0 && photos.length === 0 && (
          <Button
            className="w-full h-14 text-lg bg-emerald-500 hover:bg-emerald-600"
            onClick={handleFinish}
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Terminer ({uploadedCount} photo(s) envoyée(s))
          </Button>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          Ce lien expire dans {tokenInfo?.expires_at ? "quelques minutes" : "30 minutes"}
        </p>
      </div>
    </div>
  );
}
